import React from 'react';
import { useTranslation } from 'react-i18next';

export const hasObservationValue = value => typeof value === 'number' && Number.isFinite(value);
export const observationValue = (value, scale = 10) => hasObservationValue(value)
  ? `${value.toFixed(1)}/${scale}` : '—';

export const hasSourceObservations = source => hasObservationValue(source?.current)
  || (source?.groups || []).some(group => hasObservationValue(group.current));
export const hasCurrentObservations = card => Object.values(card?.sources || { player: card }).some(hasSourceObservations);

export function comparisonExplanation(value, en = false) {
  const reason = value?.comparison_status || value?.trend?.reason;
  const messages = {
    missing_current: ['Det finns inga svar eller bedömningar att jämföra för den här perioden.', 'There are no responses or assessments to compare for this period.'],
    missing_previous: ['Det saknas svar eller bedömningar från förra perioden.', 'There are no responses or assessments from the previous period.'],
    ongoing_period: ['Perioden pågår fortfarande. En trend kan visas när perioden är avslutad och det finns tillräckligt med jämförbara svar.', 'This period is still ongoing. A trend can be shown once it has ended and enough comparable responses are available.'],
    insufficient_samples: ['För att visa en trend behövs minst tre svar eller bedömningar i varje period.', 'A trend needs at least three responses or assessments in each period.'],
    mixed_definitions: ['Frågorna och sammanhangen visas var för sig. Varje jämförelse använder samma fråga och sammanhang.', 'Questions and contexts are shown separately. Each comparison uses the same question and context.'],
    changed_definition: ['Frågorna eller sammanhanget skiljer sig mellan perioderna. Därför visas ingen trend.', 'The questions or context differ between periods, so no trend is shown.'],
  };
  if (reason === 'comparable') return '';
  return messages[reason]?.[en ? 1 : 0] || (en ? 'There is not enough comparable data yet.' : 'Det finns ännu inte tillräckligt med jämförbara svar eller bedömningar.');
}

const sourceCount = (count, source, en) => {
  const noun = source === 'player' ? (en ? (count === 1 ? 'response' : 'responses') : 'svar')
    : (en ? (count === 1 ? 'assessment' : 'assessments') : (count === 1 ? 'bedömning' : 'bedömningar'));
  return `${count} ${noun}`;
};

function ObservationSummary({ children, comparison = false }) {
  return <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 rounded py-2 font-medium hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 [&::-webkit-details-marker]:hidden">
    <span className="min-w-0">{children}</span>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false" className={`h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none ${comparison ? 'group-open/comparison:rotate-180' : 'group-open/observation:rotate-180'}`}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  </summary>;
}

function originalScaleLabel(source) {
  if (source?.scale_kind !== 'normalized') return '';
  const scales = (source.original_scales || []).filter(item => hasObservationValue(item?.min) && hasObservationValue(item?.max));
  if ((source.scale || 10) === 10 && scales.every(item => item.min === 1 && item.max === 10)) return '';
  return [...new Set(scales.map(item => `${item.min}–${item.max}`))].sort().join(', ');
}

export function ObservationComparisonNote({ source }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  const explanation = comparisonExplanation(source, en);
  if (!explanation) return null;
  return <details className="group/comparison mt-1 text-xs text-muted-foreground">
    <ObservationSummary comparison>{en ? 'No comparison yet' : 'Ingen jämförelse ännu'}</ObservationSummary>
    <p className="pb-1 text-[11px] leading-relaxed">{explanation}</p>
  </details>;
}

export function ObservationEvidenceDate({ source }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  if (!hasSourceObservations(source)) return null;
  const dates = source?.observation_dates;
  return <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
    {dates?.complete && dates.start_date && dates.end_date
      ? <ObservationDate period={{ status: 'resolved', start_date: dates.start_date, end_date: dates.end_date }} ongoing={dates.ongoing} compact />
      : <p>{en ? 'Observation date incomplete' : 'Observationsdatum ofullständigt'}</p>}
  </div>;
}

export function ObservationSourceLabel({ card }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  const kind = card.primary_source || 'player';
  const source = card.sources?.[kind] || card;
  const otherKind = kind === 'player' ? 'coach' : 'player';
  const other = card.sources?.[otherKind];
  return <div className="mt-1 space-y-1 text-xs text-muted-foreground"><p>
    {kind === 'coach' ? (en ? 'Coach observation' : 'Ledarens observation') : (en ? 'Player reflection' : 'Spelarens reflektion')}
    {' · '}{sourceCount(source.samples ?? '—', kind, en)}
    {hasObservationValue(source.participants) && <> · {source.participants} {en ? 'players' : 'spelare'}</>}
  </p>{hasSourceObservations(other) && <p>
    {otherKind === 'coach' ? (en ? 'Coach observation' : 'Ledarens observation') : (en ? 'Player reflection' : 'Spelarens reflektion')}
    {': '}{hasObservationValue(other.current) ? observationValue(other.current, other.scale || 10) : (en ? 'see evidence' : 'se underlag')}
    {' · '}{sourceCount(other.samples ?? '—', otherKind, en)}
  </p>}</div>;
}

export function ObservationEvidenceDetails({ card }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  const sources = card.sources || { player: card };
  const visibleSources = Object.entries(sources).filter(([kind, source]) => source && (kind === (card.primary_source || 'player') || hasSourceObservations(source) || hasObservationValue(source.previous) || source.groups?.length));
  return <details className="group/observation mt-2 text-xs text-muted-foreground" data-testid="observation-evidence-details">
    <ObservationSummary>{en ? 'View evidence' : 'Visa underlag'}</ObservationSummary>
    <div className="space-y-3 pb-2">
      {visibleSources.map(([kind, source]) => <div key={kind} className="space-y-1">
        {visibleSources.length > 1 && <p className="font-medium text-foreground">{kind === 'coach' ? (en ? 'Coach observation' : 'Ledarens observation') : (en ? 'Player reflection' : 'Spelarens reflektion')}{' · '}{sourceCount(source.samples ?? '—', kind, en)}
    {hasObservationValue(source.participants) && <> · {source.participants} {en ? 'players' : 'spelare'}</>}</p>}
        {kind !== (card.primary_source || 'player') && hasObservationValue(source.current) && <p>{observationValue(source.current, source.scale || 10)}</p>}
        {source.groups?.length ? <ObservationGroupDetails source={source} kind={kind} embedded /> : <>
          <ObservationEvidenceDate source={source} />
          <ScaleDetails source={source} en={en} />
          {hasObservationValue(source.previous) && <p>{en ? 'Previous period' : 'Förra perioden'}: {observationValue(source.previous, source.scale || 10)}</p>}
          <p>{comparisonExplanation(source, en)}</p>
        </>}
      </div>)}
    </div>
  </details>;
}

export function ObservationGroupDetails({ source, kind = 'player', embedded = false }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  const groups = source?.groups || [];
  if (!groups.length) return null;
  const currentValueShown = groups.length === 1 && hasObservationValue(source.current);
  const firstScale = originalScaleLabel(groups[0]);
  const sharedScale = groups.length > 1 && firstScale && groups.every(group => originalScaleLabel(group) === firstScale) ? firstScale : '';
  const sharedExplanation = groups.length > 1 && groups[0].comparison_status !== 'comparable'
    && groups.every(group => group.comparison_status === groups[0].comparison_status)
    ? comparisonExplanation(groups[0], en) : '';
  const situations = {
    training: ['Träning', 'Training'], match: ['Match', 'Match'],
    weekly: ['Vecka', 'Week'], monthly: ['Månad', 'Month'],
    season: ['Säsong', 'Season'],
    pre_match: ['Inför match', 'Before a match'], manual: ['Manuell', 'Manual'],
  };
  const Container = embedded ? 'div' : 'details';
  return <Container className="group/observation mt-2 text-xs text-muted-foreground" data-testid="observation-groups">
    {!embedded && <ObservationSummary>
      {groups.length > 1
        ? (en ? `Show questions and contexts (${groups.length})` : `Visa frågor och sammanhang (${groups.length})`)
        : (en ? 'The question behind the value' : 'Frågan bakom värdet')}
    </ObservationSummary>}
    <div className="max-h-80 overflow-y-auto space-y-3 pb-1 pr-1">
      {groups.map(group => <div key={group.id} className="rounded-lg border border-border/40 p-3 space-y-1.5" data-testid="observation-group">
        <p className="text-[11px]">{situations[group.situation]?.[en ? 1 : 0] || (en ? 'Other context' : 'Annat sammanhang')}</p>
        <p className="font-medium text-foreground break-words">{group.question_labels?.[en ? 'en' : 'sv'] || group.question_labels?.sv || (en ? 'Saved question' : 'Sparad fråga')}</p>
        {!currentValueShown && <p>{en ? 'This period' : 'Denna period'}: <span className="font-medium text-foreground">{observationValue(group.current, group.scale || 10)}</span>
          {' · '}{sourceCount(group.samples ?? 0, kind, en)}</p>}
        {hasObservationValue(group.previous) && <p>{en ? 'Previous period' : 'Förra perioden'}: {observationValue(group.previous, group.scale || 10)}
          {' · '}{sourceCount(group.previous_samples ?? 0, kind, en)}</p>}
        <ObservationEvidenceDate source={group} kind={kind} />
        {!sharedScale && <ScaleDetails source={group} en={en} />}
        {group.comparison_status === 'comparable' && hasObservationValue(group.trend?.delta)
          ? <p>{group.trend.direction === 'stable' ? (en ? 'Similar level to the previous period.' : 'Liknande nivå som förra perioden.')
            : `${group.trend.delta > 0 ? '+' : ''}${group.trend.delta.toFixed(1)} ${en ? 'compared with the previous period.' : 'jämfört med förra perioden.'}`}</p>
          : !sharedExplanation && (embedded ? <p>{comparisonExplanation(group, en)}</p> : <ObservationComparisonNote source={group} />)}
      </div>)}
    </div>
    {(sharedScale || sharedExplanation) && <div className="mt-2 space-y-1 text-[11px] leading-relaxed">
      {sharedScale && <p>{en ? 'Original scale for these questions' : 'Ursprunglig skala för frågorna'}: {sharedScale}.</p>}
      {sharedExplanation && (embedded ? <p>{sharedExplanation}</p> : <ObservationComparisonNote source={groups[0]} />)}
    </div>}
  </Container>;
}

function ScaleDetails({ source, en }) {
  if (source?.scale_kind !== 'normalized') return null;
  const original = originalScaleLabel(source);
  return <>
    {original && <p>{en ? 'Original scale' : 'Ursprunglig skala'}: {original}.</p>}
    {source.definition_count > 1 && <p>{en ? 'This period includes different questions or contexts.' : 'Svaren gäller olika frågor eller sammanhang.'}</p>}
    {source.previous_definition_count > 1 && <p>{en ? 'The previous period includes different questions or contexts.' : 'Förra periodens svar gäller olika frågor eller sammanhang.'}</p>}
  </>;
}

// Kept as an export for existing consumers outside these evaluation views.
export function ObservationScaleNote({ source }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  if (source?.scale_kind !== 'normalized' || !hasObservationValue(source.current) || source.groups?.length) return null;
  if (!originalScaleLabel(source) && !(source.definition_count > 1) && !(source.previous_definition_count > 1)) return null;
  return <details className="group/observation mt-1 text-xs text-muted-foreground">
    <ObservationSummary>{en ? 'About the data' : 'Om underlaget'}</ObservationSummary>
    <div className="mt-1 space-y-1 leading-relaxed"><ScaleDetails source={source} en={en} /></div>
  </details>;
}

export function ObservationPeriodLabel({ weeks }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  return <>{weeks} {en ? (weeks === 1 ? 'week' : 'weeks') : (weeks === 1 ? 'vecka' : 'veckor')}</>;
}

export function ObservationDate({ period, ongoing = false, compact = false, className = 'block text-xs text-muted-foreground' }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  if (period?.status !== 'resolved') return null;
  const calendarDate = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const day = new Date(`${value}T12:00:00`);
    return Number.isNaN(day.getTime()) ? null : new Intl.DateTimeFormat(en ? 'en-GB' : 'sv-SE',
      { day: 'numeric', month: 'short', ...(compact && String(period.start_date).slice(0, 4) === String(new Date().getFullYear()) && String(period.end_date).slice(0, 4) === String(new Date().getFullYear()) ? {} : { year: 'numeric' }) }).format(day);
  };
  const start = calendarDate(period.start_date), end = calendarDate(period.end_date);
  if (!start || !end) return null;
  return <span className={className}>
    {en ? 'Refers to' : 'Avser'}: {start}{period.end_date !== period.start_date && <> – {end}</>}
    {ongoing && <> · {en ? 'ongoing' : 'pågående'}</>}
  </span>;
}

export function ObservationWindowNote({ window: period }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  return <details className="group/observation text-xs text-muted-foreground" data-testid="observation-method-info">
    <ObservationSummary>{en ? 'How the values are calculated' : 'Så beräknas värdena'}</ObservationSummary>
    <div className="mt-1 max-w-2xl space-y-2 leading-relaxed">
      <p>{en ? 'When questions use different scales, the values are converted to a common 10-point scale. Player responses and coach assessments are shown separately.' : 'När frågor har olika skalor räknas värdena om till en gemensam 10-skala. Spelarens svar och ledarens bedömningar visas var för sig.'}</p>
      <p>{en ? 'A trend needs at least three responses or assessments in each period, based on comparable questions and contexts.' : 'En trend kräver minst tre svar eller bedömningar i varje period, med jämförbara frågor och sammanhang.'}</p>
      <p>{en ? 'Different questions and contexts are shown separately. Several answers in one reflection count as one observation in the area.' : 'Olika frågor och sammanhang visas var för sig. Flera svar i samma reflektion räknas som ett underlag för området.'}</p>
      {period?.date_basis === 'observation_period' ? <>
        <p>{en ? 'Responses and assessments belong to the activity or period they refer to, even when submitted later.' : 'Svar och bedömningar hör till aktiviteten eller perioden de avser, även när de registreras senare.'}</p>
        <p>{en ? 'Week, month and season reviews are placed at the end of their stated period. Ongoing periods are visible immediately, but their trends wait until the period has ended.' : 'Vecko-, månads- och säsongsutvärderingar placeras vid periodens slut. Pågående perioder visas direkt, men jämförs först när perioden är avslutad.'}</p>
        <p>{en ? 'Responses without a reliable activity date or period remain in the history and do not contribute to these values.' : 'Svar utan säker aktivitetsdag eller period finns kvar i historiken och räknas inte in i dessa värden.'}</p>
      </> : period?.date_basis && <p>{en ? 'The selected period is based on when evaluations and assessments were created.' : 'Perioden utgår från när utvärderingarna och bedömningarna skapades.'}</p>}
    </div>
  </details>;
}

export function alignmentDisplayValue(source) {
  return hasObservationValue(source?.normalized_value) && hasObservationValue(source?.normalized_scale)
    ? observationValue(source.normalized_value, source.normalized_scale)
    : hasObservationValue(source?.scale) && source.scale > 0 ? observationValue(source?.value, source.scale) : '—';
}

export function AlignmentEvidenceNote({ card }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  const count = card?.observation_count;
  const context = ({ training: ['Träning', 'Training'], match: ['Match', 'Match'], weekly: ['Vecka', 'Week'], monthly: ['Månad', 'Month'] })[card?.situation]?.[en ? 1 : 0];
  const question = card?.question_labels?.player?.[en ? 'en' : 'sv'] || card?.question_labels?.player?.sv;
  return <div className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
    {context && <p className="font-medium text-foreground">{context}</p>}
    {question && <p className="mt-0.5 break-words">{question}</p>}
    <p className="mt-1">
    {hasObservationValue(card?.coach?.normalized_value) && (en ? 'Both views shown on a common 10-point scale. ' : 'Båda bilderna visas på en gemensam 10-skala. ')}
    {hasObservationValue(count) ? `${count} ${en ? 'matched occasions' : 'matchade tillfällen'}` : `${card?.player?.samples ?? '—'} ${en ? 'player observations' : 'spelarobservationer'} · ${card?.coach?.samples ?? '—'} ${en ? 'coach observations' : 'ledarobservationer'}`}.
    </p>
  </div>;
}

export function AlignmentAvailabilityNote({ section }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  if (!section?.excluded_mixed_cores?.length && !['mixed_definitions', 'partial'].includes(section?.comparison_status)) return null;
  return <p className="text-[11px] leading-relaxed text-muted-foreground">{en
    ? 'Some areas contain different questions or contexts and are not compared. The separate observations remain visible.'
    : 'Vissa områden innehåller olika frågor eller sammanhang och jämförs därför inte. De separata observationerna finns kvar.'}</p>;
}

export function ObservationSourceNote({ card }) {
  const { i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  if (!card.sources) return null;
  const primary = card.primary_source || 'player';
  const other = primary === 'player' ? 'coach' : 'player';
  const label = s => s === 'player' ? (en ? 'Player reflection' : 'Spelarens reflektion') : (en ? 'Coach observation' : 'Ledarens observation');
  const secondary = card.sources[other];
  return <div className="mt-1 text-xs text-muted-foreground">
    <p>{label(primary)}</p>
    <ObservationEvidenceDate source={card.sources[primary]} kind={primary} />
    {card.sources[primary]?.aggregation_status === 'separate_groups' && <p>{en ? 'Shown by question and context.' : 'Visas per fråga och sammanhang.'}</p>}
    <ObservationGroupDetails source={card.sources[primary]} kind={primary} />
    {hasSourceObservations(secondary) && <>
      <p>{label(other)}: {hasObservationValue(secondary.current)
        ? `${observationValue(secondary.current, secondary.scale || 10)} · ${sourceCount(secondary.samples ?? 0, other, en)}`
        : (en ? 'shown separately' : 'visas uppdelat')}</p>
      <ObservationEvidenceDate source={secondary} kind={other} />
      <ObservationGroupDetails source={secondary} kind={other} />
    </>}
    <ObservationScaleNote source={card.sources[primary]} />
  </div>;
}

function QuestionGroupSummary({ source, en }) {
  const groups = (source.groups || []).filter(group => hasObservationValue(group.current));
  if (groups.length < 2) return null;
  const names = { training: ['Träning', 'Training'], match: ['Match', 'Match'], weekly: ['Vecka', 'Week'], monthly: ['Månad', 'Month'] };
  return <div className="mt-2 space-y-2">
    {groups.slice(0, 4).map(group => <div key={group.id} className="rounded-lg border border-border/40 p-2.5 text-xs">
      <p className="text-[10px] text-muted-foreground">{names[group.situation]?.[en ? 1 : 0] || (en ? 'Questionnaire' : 'Formulär')}</p>
      <p className="mt-1 break-words">{group.question_labels?.[en ? 'en' : 'sv'] || group.question_labels?.sv}</p>
      <p className="mt-1 font-medium tabular-nums">{observationValue(group.current, group.scale || 10)}
        <span className="ml-2 text-[10px] font-normal text-muted-foreground">{en ? 'response average' : 'genomsnitt av svar'}</span></p>
      <p className="mt-0.5 text-[10px] text-muted-foreground">{group.samples} {en ? 'responses' : 'svar'}
        {hasObservationValue(group.participants) && <> · {group.participants} {en ? 'players' : 'spelare'}</>}
      </p>
    </div>)}
    {groups.length > 4 && <p className="text-[11px] text-muted-foreground">{en ? 'More questions under View evidence.' : 'Fler frågor under Visa underlag.'}</p>}
  </div>;
}

export default function ObservationCard({ card }) {
  const { t, i18n } = useTranslation();
  const en = (i18n.resolvedLanguage || i18n.language)?.startsWith('en');
  if (!card) return null;
  const sources = card.sources || { player: card };
  const hasCurrent = Object.values(sources).some(hasSourceObservations);
  return <div className="min-w-0 self-start rounded-xl border border-border/40 bg-card/60 p-3.5" data-testid={`observation-${card.core_metric}`}
      data-empty={Object.values(sources).every(value => !hasSourceObservations(value) && !hasObservationValue(value?.previous) && !(value?.groups || []).some(group => hasObservationValue(group.previous)))}>
    <p className="text-xs font-medium">{t(`development:eval_status_v2.core_metrics.${card.core_metric}`, { defaultValue: card.label })}</p>
    {Object.entries(sources).filter(([key, value]) => value && (key === 'player' || hasSourceObservations(value) || hasObservationValue(value.previous) || value.records_in_period > 0 || value.groups?.length > 0)).map(([source, value]) => <div key={source} className="mt-2">
      {hasObservationValue(value.current)
        ? <p className="text-lg font-semibold">{observationValue(value.current, value.scale || 10)}</p>
        : <p className="text-xs text-muted-foreground">{value.aggregation_status === 'separate_groups'
          ? (en ? 'Responses by question and context' : 'Svar per fråga och sammanhang')
          : value.records_in_period > 0
            ? (en ? 'No measured value for this area' : 'Saknar mätvärde för området')
            : (en ? 'No observations this period' : 'Inget underlag under perioden')}</p>}
      <ObservationSourceLabel card={{ ...value, primary_source: source }} />
      <QuestionGroupSummary source={value} en={en} />
      {card.is_status && <ObservationEvidenceDate source={value} />}
      {value.comparison_status === 'comparable' && hasObservationValue(value.trend?.delta) && <p className="mt-1 text-xs text-muted-foreground">
        {value.trend.delta > 0 ? '+' : ''}{value.trend.delta.toFixed(1)} {en ? 'vs. previous period' : 'mot föregående period'}
      </p>}
    </div>)}
    <ObservationEvidenceDetails card={card} />
    {hasCurrent && (card.higher_is_worse || (card.is_status && ['pain', 'fatigue'].includes(card.core_metric))) && <p className="mt-2 text-xs text-muted-foreground">{card.core_metric === 'fatigue'
      ? (en ? 'Higher value = more fatigue' : 'Högre värde = mer trötthet')
      : card.core_metric === 'pain' ? (en ? 'Higher value = more pain' : 'Högre värde = mer smärta')
        : (en ? 'Higher value = more symptoms' : 'Högre värde = mer besvär')}</p>}
  </div>;
}
